import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Wallet, RefreshCw, TrendingUp, TrendingDown, DatabaseZap,
  Send, X, ChevronDown, ChevronRight, Layers,
} from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';
import { useToast } from '../components/Toast';

const BOT_ID = 'vera';

// ─── Tipler ───────────────────────────────────────────────────────────────────

interface Position {
  symbol: string; side: string; positionQty: number;
  avgEntryPrice: number; markPrice: number; unrealizedPnl: number;
  pnlPct: number; leverage: number; notional: number;
}
interface OpenOrder {
  hash: string; symbol: string; side: string;
  price: string; quantity: string; filledQty: string;
}
interface Account {
  totalAccountValue: number; availableBalance: number;
  crossUnrealizedPnl: number; usdc_balance: number;
}

// ─── Yardımcılar ──────────────────────────────────────────────────────────────

const fmtSym = (s: string) =>
  (s || '').replace('PERP_','').replace('_USDC','').replace('-PERP','').replace('PERP','');

const fmtUsd = (n: number, d = 2) =>
  `${n >= 0 ? '+' : ''}$${Math.abs(n).toFixed(d)}`;

// ─── Bileşen ──────────────────────────────────────────────────────────────────

export default function VeraBot({ dark = true }: { dark?: boolean }) {
  const { connected, sendForBot, subscribe } = useWS() as any;
  const { showToast } = useToast();
  const t = getTheme(dark);

  // State
  const [account,    setAccount]    = useState<Account | null>(null);
  const [positions,  setPositions]  = useState<Position[]>([]);
  const [openOrders, setOpenOrders] = useState<OpenOrder[]>([]);
  const [tickers,    setTickers]    = useState<Record<string, any>>({});
  const [loading,    setLoading]    = useState(false);
  const [backfilling,setBackfilling]= useState(false);
  const [posOpen,    setPosOpen]    = useState(true);
  const [ordOpen,    setOrdOpen]    = useState(true);

  // Emir formu
  const [sym,      setSym]      = useState('BTC-PERP');
  const [side,     setSide]     = useState<'long'|'short'>('long');
  const [price,    setPrice]    = useState('');
  const [qty,      setQty]      = useState('');
  const [lev,      setLev]      = useState('10');
  const [placing,  setPlacing]  = useState(false);

  // Mesaj dinleyici — sadece vera mesajlarını işle
  useEffect(() => {
    return subscribe((data: any) => {
      if (data.bot_id !== BOT_ID) return;

      if (data.type === 'account')     setAccount(data.payload);
      if (data.type === 'positions')   setPositions(data.payload || []);
      if (data.type === 'open_orders') setOpenOrders(data.payload || []);
      if (data.type === 'tickers') {
        const m: Record<string, any> = {};
        (data.payload || []).forEach((tk: any) => { m[tk.symbol] = tk; });
        setTickers(m);
        setLoading(false);
      }
      if (data.type === 'backfill_progress') {
        if (data.payload?.status === 'done') {
          setBackfilling(false);
          showToast(
            'Vera Backfill Tamamlandı',
            `${data.payload.trades ?? 0} işlem, ${data.payload.funding ?? 0} funding kaydedildi`,
            'success',
          );
        }
        if (data.payload?.status === 'error') {
          setBackfilling(false);
          showToast('Backfill Hatası', data.payload.error || '', 'error');
        }
      }
      if (data.type === 'manual_order_result') {
        setPlacing(false);
        if (data.payload?.success) showToast('Emir Gönderildi', '', 'success');
        else showToast('Emir Reddedildi', data.payload?.error || '', 'error');
      }
    });
  }, [subscribe, showToast]);

  // İlk yüklemede verileri çek
  const refresh = useCallback(() => {
    if (!connected) return;
    setLoading(true);
    sendForBot({ type: 'get_account' },      BOT_ID);
    sendForBot({ type: 'get_open_orders' },  BOT_ID);
  }, [connected, sendForBot]);

  useEffect(() => { if (connected) refresh(); }, [connected]); // eslint-disable-line

  // Backfill — 3 Mart 2026'dan itibaren
  const handleBackfill = () => {
    if (!connected) return;
    setBackfilling(true);
    sendForBot({ type: 'backfill_history', payload: { start_t: 1772496000000 } }, BOT_ID);
  };

  // Emir gönder
  const handleOrder = async () => {
    if (!price || !qty || placing) return;
    setPlacing(true);
    sendForBot({
      type: 'manual_order',
      payload: {
        symbol:   sym,
        side,
        price:    parseFloat(price),
        quantity: parseFloat(qty),
        leverage: parseInt(lev),
        postOnly: true,
      },
    }, BOT_ID);
  };

  // Emir iptal
  const cancelOrder = (hash: string, symbol: string) => {
    sendForBot({ type: 'cancel_order', payload: { symbol, orderHashes: [hash] } }, BOT_ID);
    setOpenOrders(prev => prev.filter(o => o.hash !== hash));
  };

  // Mark fiyatını sym'dan bul
  const markPrice = useMemo(() => {
    const key = sym.replace('-PERP', '-PERP').replace('PERP_','').replace('_USDC','');
    return tickers[sym]?.mark_price || tickers[key]?.mark_price || 0;
  }, [tickers, sym]);

  // Stil yardımcıları
  const card     = `${t.card} border ${t.cardBorder} rounded-xl`;
  const input    = `w-full bg-[#0a1628] border border-[#1a3060] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#4A9FE8] placeholder-blue-900`;
  const colHdr   = `px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide ${t.muted}`;
  const cell     = `px-3 py-2 text-xs`;

  const equity   = account?.totalAccountValue ?? 0;
  const avail    = account?.availableBalance   ?? 0;
  const upnl     = account?.crossUnrealizedPnl ?? 0;

  return (
    <div className="p-6 space-y-5">

      {/* ── Başlık ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#4DA2FF]/20 to-[#4DA2FF]/5 border border-[#4DA2FF]/30 flex items-center justify-center">
            <Wallet size={18} className="text-[#4DA2FF]" />
          </div>
          <div>
            <h2 className={`text-xl font-bold ${t.text}`}>Vera Wallet</h2>
            <p className={`text-[11px] ${t.muted}`}>Bluefin · Sui Network</p>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${t.muted} ${t.cardBorder}`}>bot: vera</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 text-xs ${t.muted}`}>
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-rose-400'}`} />
            {connected ? 'Canlı' : 'Bağlantı bekleniyor…'}
          </div>
          <button
            onClick={handleBackfill}
            disabled={backfilling || !connected}
            title="3 Mart 2026'dan itibaren tüm Vera işlemlerini SQLite'a aktar"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-blue-500/60 text-blue-400 hover:bg-blue-500/10 disabled:opacity-40 transition-colors"
          >
            <DatabaseZap className={`size-3.5 ${backfilling ? 'animate-pulse' : ''}`} />
            {backfilling ? 'Aktarılıyor…' : 'Backfill'}
          </button>
          <button
            onClick={refresh}
            disabled={loading || !connected}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${t.btnSecondary} disabled:opacity-40`}
          >
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </button>
        </div>
      </div>

      {/* ── Hesap Özeti ── */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Equity',            val: `$${equity.toFixed(2)}`,            clr: t.text },
          { label: 'Kullanılabilir',    val: `$${avail.toFixed(2)}`,             clr: t.text },
          { label: 'Unrealized PnL',   val: fmtUsd(upnl),                       clr: upnl >= 0 ? t.positive : t.negative },
        ].map(c => (
          <div key={c.label} className={`${card} p-4`}>
            <p className={`text-xs mb-1 ${t.muted}`}>{c.label}</p>
            <p className={`text-lg font-bold font-mono ${c.clr}`}>{c.val}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-5 gap-5">

        {/* ── Sol: Pozisyonlar + Açık Emirler ── */}
        <div className="col-span-3 space-y-4">

          {/* Pozisyonlar */}
          <div className={`${card} overflow-hidden`}>
            <button
              onClick={() => setPosOpen(v => !v)}
              className={`w-full px-4 py-3 flex items-center justify-between hover:opacity-80 ${posOpen ? `border-b ${t.cardBorder}` : ''}`}
            >
              <div className="flex items-center gap-2">
                <Layers size={14} className={t.gold} />
                <span className={`text-sm font-semibold ${t.text}`}>Pozisyonlar</span>
                {positions.length > 0 && (
                  <span className="text-[10px] bg-[#4DA2FF]/20 text-[#4DA2FF] px-1.5 py-0.5 rounded-full">
                    {positions.length}
                  </span>
                )}
              </div>
              {posOpen ? <ChevronDown size={14} className={t.muted} /> : <ChevronRight size={14} className={t.muted} />}
            </button>

            {posOpen && (
              positions.length === 0 ? (
                <p className={`px-4 py-8 text-center text-xs ${t.muted}`}>Açık pozisyon yok.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className={`border-b ${t.cardBorder} ${t.tableHeader}`}>
                        <th className={colHdr}>Sembol</th>
                        <th className={colHdr}>Yön</th>
                        <th className={`${colHdr} text-right`}>Miktar</th>
                        <th className={`${colHdr} text-right`}>Giriş</th>
                        <th className={`${colHdr} text-right`}>Mark</th>
                        <th className={`${colHdr} text-right`}>PnL</th>
                        <th className={`${colHdr} text-right`}>Lev</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map(p => (
                        <tr key={p.symbol} className={`border-b ${t.tableRow} ${t.cardBorder}`}>
                          <td className={`${cell} font-semibold ${t.gold}`}>{fmtSym(p.symbol)}</td>
                          <td className={cell}>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                              p.side === 'long' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                            }`}>{p.side?.toUpperCase()}</span>
                          </td>
                          <td className={`${cell} text-right font-mono ${t.text}`}>{Math.abs(p.positionQty || 0).toFixed(4)}</td>
                          <td className={`${cell} text-right font-mono ${t.muted}`}>${(p.avgEntryPrice || 0).toFixed(2)}</td>
                          <td className={`${cell} text-right font-mono ${t.text}`}>${(p.markPrice || 0).toFixed(2)}</td>
                          <td className={`${cell} text-right font-mono font-bold ${(p.unrealizedPnl || 0) >= 0 ? t.positive : t.negative}`}>
                            {fmtUsd(p.unrealizedPnl || 0, 4)}
                            <span className={`text-[10px] ml-1 ${t.muted}`}>({(p.pnlPct || 0).toFixed(2)}%)</span>
                          </td>
                          <td className={`${cell} text-right ${t.muted}`}>{p.leverage}x</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>

          {/* Açık Emirler */}
          <div className={`${card} overflow-hidden`}>
            <button
              onClick={() => setOrdOpen(v => !v)}
              className={`w-full px-4 py-3 flex items-center justify-between hover:opacity-80 ${ordOpen ? `border-b ${t.cardBorder}` : ''}`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-sm font-semibold ${t.text}`}>Açık Emirler</span>
                {openOrders.length > 0 && (
                  <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">
                    {openOrders.length}
                  </span>
                )}
              </div>
              {ordOpen ? <ChevronDown size={14} className={t.muted} /> : <ChevronRight size={14} className={t.muted} />}
            </button>

            {ordOpen && (
              openOrders.length === 0 ? (
                <p className={`px-4 py-8 text-center text-xs ${t.muted}`}>Açık emir yok.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className={`border-b ${t.cardBorder} ${t.tableHeader}`}>
                        <th className={colHdr}>Sembol</th>
                        <th className={colHdr}>Yön</th>
                        <th className={`${colHdr} text-right`}>Fiyat</th>
                        <th className={`${colHdr} text-right`}>Miktar</th>
                        <th className={`${colHdr} text-right`}>Dolu</th>
                        <th className={colHdr}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {openOrders.map(o => (
                        <tr key={o.hash} className={`border-b ${t.tableRow} ${t.cardBorder}`}>
                          <td className={`${cell} font-semibold ${t.gold}`}>{fmtSym(o.symbol)}</td>
                          <td className={cell}>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                              o.side === 'LONG' || o.side === 'BUY'
                                ? 'bg-emerald-900/50 text-emerald-400'
                                : 'bg-rose-900/50 text-rose-400'
                            }`}>{o.side}</span>
                          </td>
                          <td className={`${cell} text-right font-mono ${t.text}`}>${parseFloat(o.price || '0').toFixed(2)}</td>
                          <td className={`${cell} text-right font-mono ${t.muted}`}>{o.quantity}</td>
                          <td className={`${cell} text-right font-mono ${t.muted}`}>{o.filledQty || '0'}</td>
                          <td className={cell}>
                            <button
                              onClick={() => cancelOrder(o.hash, o.symbol)}
                              className="text-rose-400 hover:text-rose-300 transition-colors"
                            >
                              <X size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>

        {/* ── Sağ: Emir Formu ── */}
        <div className="col-span-2">
          <div className={`${card} p-4 space-y-4`}>
            <h3 className={`text-sm font-semibold ${t.text}`}>Manuel Emir</h3>

            {/* Sembol */}
            <div>
              <label className={`block text-[11px] mb-1 ${t.muted}`}>Sembol</label>
              <input
                className={input}
                value={sym}
                onChange={e => setSym(e.target.value.toUpperCase())}
                placeholder="BTC-PERP"
              />
              {markPrice > 0 && (
                <p className={`text-[10px] mt-1 ${t.muted}`}>
                  Mark: <span className={t.text}>${markPrice.toFixed(2)}</span>
                </p>
              )}
            </div>

            {/* Yön */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setSide('long')}
                className={`py-2 rounded-lg text-xs font-bold transition-colors ${
                  side === 'long'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-emerald-900/20 text-emerald-600 hover:bg-emerald-900/40'
                }`}
              >
                <TrendingUp size={13} className="inline mr-1" />LONG
              </button>
              <button
                onClick={() => setSide('short')}
                className={`py-2 rounded-lg text-xs font-bold transition-colors ${
                  side === 'short'
                    ? 'bg-rose-600 text-white'
                    : 'bg-rose-900/20 text-rose-600 hover:bg-rose-900/40'
                }`}
              >
                <TrendingDown size={13} className="inline mr-1" />SHORT
              </button>
            </div>

            {/* Fiyat */}
            <div>
              <label className={`block text-[11px] mb-1 ${t.muted}`}>Fiyat (USDC)</label>
              <input
                className={input}
                type="number"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder={markPrice > 0 ? markPrice.toFixed(2) : '0.00'}
              />
            </div>

            {/* Miktar */}
            <div>
              <label className={`block text-[11px] mb-1 ${t.muted}`}>Miktar</label>
              <input
                className={input}
                type="number"
                value={qty}
                onChange={e => setQty(e.target.value)}
                placeholder="0.01"
              />
              {price && qty && (
                <p className={`text-[10px] mt-1 ${t.muted}`}>
                  Notional: <span className={t.text}>${(parseFloat(price || '0') * parseFloat(qty || '0')).toFixed(2)}</span>
                </p>
              )}
            </div>

            {/* Kaldıraç */}
            <div>
              <label className={`block text-[11px] mb-1 ${t.muted}`}>Kaldıraç</label>
              <div className="flex gap-1.5 flex-wrap">
                {['1','2','3','5','10','20'].map(l => (
                  <button
                    key={l}
                    onClick={() => setLev(l)}
                    className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                      lev === l
                        ? 'bg-[#4A9FE8] text-white'
                        : `${t.cardBorder} border ${t.muted} hover:border-[#4A9FE8]`
                    }`}
                  >
                    {l}x
                  </button>
                ))}
              </div>
            </div>

            {/* Gönder */}
            <button
              onClick={handleOrder}
              disabled={placing || !price || !qty || !connected}
              className={`w-full py-2.5 rounded-lg text-sm font-bold transition-colors disabled:opacity-40 flex items-center justify-center gap-2 ${
                side === 'long'
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  : 'bg-rose-600 hover:bg-rose-500 text-white'
              }`}
            >
              {placing ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Send size={14} />
              )}
              {placing ? 'Gönderiliyor…' : `${side === 'long' ? 'LONG' : 'SHORT'} ${lev}x Emir Ver`}
            </button>

            <p className={`text-[10px] text-center ${t.muted}`}>Post-Only · Bluefin Sui</p>
          </div>
        </div>
      </div>
    </div>
  );
}
